/**
 * Push Statistics Component
 * Displays push notification statistics in card layout
 * Matches the old admin page statistics display
 */

import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { usePushStats } from '../hooks/usePushNotifications';
import { useI18n } from '@/i18n';

export function PushStats() {
  const { messages } = useI18n();
  const t = messages.pushNotifications;
  const { data: stats, isLoading, error } = usePushStats();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <div className="animate-pulse p-4">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="p-4 text-red-600 text-sm">
          Failed to load statistics: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  // Stats from backend format: { tokens: {...}, logs: {...} }
  const { tokens, logs } = stats;

  const statCards = [
    {
      title: t.stats.totalTokens,
      value: tokens.total.toLocaleString(),
      subtitle: `${tokens.active} ${t.tokens.active.toLowerCase()}`,
      icon: '📱',
      bgColor: 'bg-indigo-50',
      iconBg: 'bg-indigo-100',
    },
    {
      title: t.stats.activeTokens,
      value: tokens.active.toLocaleString(),
      subtitle: `${tokens.inactive} ${t.tokens.inactive.toLowerCase()}`,
      icon: '✓',
      bgColor: 'bg-green-50',
      iconBg: 'bg-green-100',
    },
    {
      title: t.stats.pushSent,
      value: logs.sent.toLocaleString(),
      subtitle: `${logs.failed} ${t.sendForm.failed.toLowerCase()}`,
      icon: '📤',
      bgColor: 'bg-blue-50',
      iconBg: 'bg-blue-100',
    },
    {
      title: t.stats.successRate,
      value: `${logs.success_rate.toFixed(1)}%`,
      subtitle: t.stats.successRate,
      icon: '✅',
      bgColor: 'bg-purple-50',
      iconBg: 'bg-purple-100',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.subtitle}</p>
              </div>
              <div className={`w-12 h-12 ${stat.iconBg} rounded-full flex items-center justify-center`}>
                <span className="text-xl">{stat.icon}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Platform Breakdown */}
      <Card>
        <CardHeader title={t.stats.platformBreakdown} />
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl mb-1">🍎</div>
              <div className="text-sm text-gray-600">{t.stats.ios}</div>
              <div className="text-lg font-semibold text-gray-900">
                {tokens.ios.toLocaleString()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">🤖</div>
              <div className="text-sm text-gray-600">{t.stats.android}</div>
              <div className="text-lg font-semibold text-gray-900">
                {tokens.android.toLocaleString()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">🌐</div>
              <div className="text-sm text-gray-600">Web</div>
              <div className="text-lg font-semibold text-gray-900">
                0
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
